/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, ReactNode } from 'react';

import { MenuItem, Select, Stack, Typography, TypographyVariant } from '@mui/material';

import { ArrowRoundedIcon } from '../svg-icons/ArrowRoundedIcon';
import { useSelectWithDescriptionStyles } from './SelectWithDescription.styles';

type Props = {
  onSubmit: (value: string) => Promise<unknown>;
  value: string | undefined;
  items: { key: string; title: string; description: ReactNode }[];
  disableUnderline?: boolean;
  typographyVariant: TypographyVariant;
  'data-cy'?: string;
  disabled?: boolean;
};

export const SelectWithDescription = memo<Props>(
  ({ typographyVariant, value: selectedValue, items, onSubmit, disabled, ...props }) => {
    const classes = useSelectWithDescriptionStyles();

    return (
      <Select
        classes={{ root: classes.select }}
        {...props}
        value={selectedValue}
        onChange={e => onSubmit(e.target.value as string)}
        IconComponent={ArrowRoundedIcon}
        disabled={disabled}
        renderValue={selectedKey => (
          <Typography variant={typographyVariant}>
            {typeof selectedKey === 'string' && items.find(item => item.key === selectedKey)?.title}
          </Typography>
        )}
      >
        {items.map(item => (
          <MenuItem key={item.key} value={item.key} data-cy={`select-with-description-${item.key}`}>
            <Stack>
              <Typography variant="subtitle1">{item.title}</Typography>
              <Typography variant="caption">{item.description}</Typography>
            </Stack>
          </MenuItem>
        ))}
      </Select>
    );
  },
);
